import type {
    FieldErrorTextProps,
    FieldLabelProps,
    FieldRootProps,
    InputElementProps,
    InputProps,
} from "@chakra-ui/react";
import { Field, GridItem, Input } from "@chakra-ui/react";
import { type ReactNode, useEffect } from "react";
import type {
    FieldValues,
    Path,
    PathValue,
    RegisterOptions,
    UseFormRegister,
    UseFormSetValue,
} from "react-hook-form";
import type { InputGroupProps } from "./components/ui/input-group";
import { InputGroup } from "./components/ui/input-group";

interface FormInputProps<TFieldValues extends FieldValues> {
    label: string;
    id: Path<TFieldValues>;
    errorType?: string;
    errorMessage?: string;
    required?: boolean;
    fieldNote?: string;
    inputProps?: InputProps;
    formControlProps?: FieldRootProps;
    formLabelProps?: FieldLabelProps;
    pattern?: RegExp;
    register: UseFormRegister<TFieldValues>;
    validate?: (val: string) => string | undefined;
    colSpan?: number;
    setValue?: UseFormSetValue<TFieldValues>; // Add setValue here
    value?: string; // Optional value prop if you want to set the value directly
    leftElement?: ReactNode;
    rightElement?: ReactNode;
    leftElementProps?: InputElementProps;
    rightElementProps?: InputElementProps;
    inputGroupProps?: Omit<InputGroupProps, "children">;
    coerceToNumber?: boolean;
    errorTextProps?: FieldErrorTextProps;
}

const FormInput = <TFieldValues extends FieldValues>({
    label,
    id,
    errorType,
    errorMessage,
    inputProps,
    formControlProps,
    formLabelProps,
    pattern,
    register,
    required = true,
    fieldNote,
    validate,
    colSpan,
    setValue, // Add setValue to props
    value, // Add value to props
    leftElement,
    leftElementProps,
    rightElement,
    rightElementProps,
    inputGroupProps,
    coerceToNumber,
    errorTextProps,
}: FormInputProps<TFieldValues>) => {
    useEffect(() => {
        if (value) {
            setValue?.(
                id,
                value as PathValue<TFieldValues, Path<TFieldValues>>,
            );
        } else {
            setValue?.(id, "" as PathValue<TFieldValues, Path<TFieldValues>>);
        }
    }, [value, id, setValue]);

    const getErrorMessage = () => {
        if (errorType === "required") return `*${label} is required`;
        if (errorType === "pattern") return `*Enter valid ${label}`;
        if (errorType === "validate") return `*${label} doesn't match`;
        return null;
    };

    const displayErrorMessage = errorMessage || getErrorMessage();

    return (
        <GridItem colSpan={colSpan} w={"100%"}>
            <Field.Root
                {...formControlProps}
                required={required}
                invalid={!!displayErrorMessage}
            >
                <Field.Label fontSize={"xs"} {...formLabelProps}>
                    {label}
                    <Field.RequiredIndicator />
                    {fieldNote ? `(${fieldNote})` : null}
                </Field.Label>
                <InputGroup
                    w={"100%"}
                    {...inputGroupProps}
                    startElement={
                        leftElement || undefined // verify this logic
                    }
                    endElement={
                        rightElement || undefined // verify this logic
                    }
                    startElementProps={{
                        ...leftElementProps,
                    }}
                    endElementProps={{
                        ...rightElementProps,
                    }}
                >
                    <Input
                        {...register(id, {
                            required,
                            pattern,
                            validate,
                            valueAsNumber: coerceToNumber,
                        } as RegisterOptions<TFieldValues, Path<TFieldValues>>)}
                        {...inputProps}
                        fontSize={"xs"}
                    />
                </InputGroup>
                {displayErrorMessage && (
                    <Field.ErrorText fontSize={"xs"} {...errorTextProps}>
                        {displayErrorMessage}
                    </Field.ErrorText>
                )}
            </Field.Root>
        </GridItem>
    );
};

export default FormInput;
